// Services mounted under a base path report their version from a live
// endpoint; the rest carry the version recorded at deploy time
export const versionOf = (service, result) => {
  if (!service.base_path) return service.version ?? null;
  return result?.status === "ok" ? result.version ?? null : null;
};

export const SERVICE_STATUS = {
  LOADING: "loading",
  ERROR: "error",
  OUTDATED: "outdated",
  LATEST: "latest",
};

// Null when there is no version, or no latest release, to compare
export const statusOf = (service, result, latestVersion) => {
  if (service.base_path && (!result || result.status === "loading")) {
    return SERVICE_STATUS.LOADING;
  }
  if (result?.status === "error") return SERVICE_STATUS.ERROR;
  const version = result?.status === "ok" ? result.version : null;
  if (!latestVersion || !version) return null;
  return version === latestVersion
    ? SERVICE_STATUS.LATEST
    : SERVICE_STATUS.OUTDATED;
};

// Ascending puts the services needing attention first
const STATUS_RANK = {
  [SERVICE_STATUS.ERROR]: 0,
  [SERVICE_STATUS.OUTDATED]: 1,
  [SERVICE_STATUS.LATEST]: 2,
};

const SORT_VALUES = {
  name: (service) => service.name,
  version: (service, result) => versionOf(service, result),
  status: (service, result, latestVersion) =>
    STATUS_RANK[statusOf(service, result, latestVersion)],
  deployed_at: (service) =>
    service.deployed_at ? Date.parse(service.deployed_at) : null,
  stack_status: (service) => service.stack_status,
};

const compareValues = (a, b) =>
  typeof a === "number" && typeof b === "number"
    ? a - b
    : String(a).localeCompare(String(b), undefined, { numeric: true });

// Blank values sort last in either direction, ties fall back to the name
export const sortServices = (
  services,
  versions,
  latestVersion,
  { key, direction }
) => {
  const valueOf = (service) =>
    SORT_VALUES[key](service, versions[service.name], latestVersion) ?? null;
  const sign = direction === "desc" ? -1 : 1;
  return [...services].sort((a, b) => {
    const valueA = valueOf(a);
    const valueB = valueOf(b);
    if (valueA === null || valueB === null) {
      if (valueA !== valueB) return valueA === null ? 1 : -1;
    } else {
      const order = compareValues(valueA, valueB);
      if (order !== 0) return sign * order;
    }
    return a.name.localeCompare(b.name);
  });
};
